/**
 * スコアレーダーチャート
 *
 * 5項目のスコアをレーダーチャートで可視化
 */

import React from 'react';
import { Box, Typography, Paper, Grid, LinearProgress, Chip } from '@mui/material';

/**
 * シンプルなスコアバー（レーダーチャートの代替）
 *
 * Note: 本格的なレーダーチャートにはrechartsやchart.jsを使用
 * 現在はシンプルなバー表示で実装
 */
export default function ScoreRadarChart({ scores }) {
  if (!scores) return null;

  // デリバリースコア項目
  const deliveryItems = [
    { key: 'response_latency', label: '応答速度', score: scores.delivery_scores?.response_latency?.score || 0 },
    { key: 'filler_rate', label: 'フィラー', score: scores.delivery_scores?.filler_rate?.score || 0 },
    { key: 'pitch_stability', label: '声の安定性', score: scores.delivery_scores?.pitch_stability?.score || 0 },
    { key: 'pause_distribution', label: 'ポーズ', score: scores.delivery_scores?.pause_distribution?.score || 0 },
    { key: 'self_correction_rate', label: '言い直し', score: scores.delivery_scores?.self_correction_rate?.score || 0 },
  ];

  // コンテンツスコア項目
  const contentItems = [
    { key: 'logical_consistency', label: '論理性', score: scores.content_scores?.logical_consistency?.score || 0 },
    { key: 'keyword_relevance', label: 'キーワード', score: scores.content_scores?.keyword_relevance?.score || 0 },
    { key: 'prosody_sync', label: '韻律同調', score: scores.content_scores?.prosody_sync?.score || 0 },
  ];

  // スコアの色を決定
  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  // スコアバーの色を決定
  const getProgressColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* デリバリースコア */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                話し方（デリバリー）
              </Typography>
              <Chip
                label={`${scores.delivery_composite_score}点`}
                color={getScoreColor(scores.delivery_composite_score)}
                size="small"
              />
            </Box>
            {deliveryItems.map((item) => (
              <Box key={item.key} sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {item.score}点
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={item.score}
                  color={getProgressColor(item.score)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* コンテンツスコア */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                内容（コンテンツ）
              </Typography>
              <Chip
                label={`${scores.content_composite_score}点`}
                color={getScoreColor(scores.content_composite_score)}
                size="small"
              />
            </Box>
            {contentItems.map((item) => (
              <Box key={item.key} sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {item.score}点
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={item.score}
                  color={getProgressColor(item.score)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>

      {/* 総合スコア */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="h4" color="primary" fontWeight="bold">
          {scores.overall_audio_score}
          <Typography component="span" variant="h6" color="text.secondary">
            /100
          </Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          総合音声スコア
        </Typography>
      </Box>
    </Box>
  );
}
